import React, { useState, useEffect, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '../services/supabase';
import { gameDayToShortDate, gameDayToMediumDate, gameDayToDate, formatSimDate, formatSimDateShort, leagueDateDisplay } from '../utils/dateUtils';
import { numberToGrade } from '../utils/grades';
import { TRAD_ARRAY } from '../constants/leagueTypes';
import BoxScore from '../components/BoxScore';
import './HomePageV2.css';

interface LeagueFromDB {
  id: string;
  name: string;
  tier: number;
  status: string;
  league_type?: string;
  entry_fee?: number;
  prize_pool?: number;
  max_teams?: number;
  current_day?: number;
  current_year?: number;
  season_year?: number;
}

interface StandingFromDB {
  team_id: string;
  wins: number;
  losses: number;
  runs_scored: number;
  runs_allowed: number;
  division: string;
  conference: string;
  league_id: string;
}

interface TeamFromDB {
  id: string;
  name: string;
  city: string;
  abbreviation: string;
}

interface GameFromDB {
  id: string;
  game_id?: string;
  league_id: string;
  home_team_id: string;
  away_team_id: string;
  home_score: number;
  away_score: number;
  game_day: number;
  status: string;
  winner: string;
  is_playoff?: boolean;
}

interface LeaderPlayer {
  player_id: string;
  home_runs?: number;
  hits?: number;
  rbi?: number;
  runs?: number;
  at_bats?: number;
  wins?: number;
  strikeouts_pitched?: number;
  earned_runs?: number;
  innings_pitched?: number;
  players: {
    first_name: string;
    last_name: string;
    position: string;
    team_id: string;
  } | null;
}

interface FreeAgent {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  overall: number;
  is_hitter: boolean;
  power?: number;
  contact?: number;
  speed?: number;
  fielding?: number;
  velocity?: number;
  control?: number;
  movement?: number;
  endurance?: number;
}

// Cached homepage data for offline resilience
interface CachedHomeData {
  leagues: LeagueFromDB[];
  standings: [string, StandingFromDB[]][];
  teams: [string, TeamFromDB][];
  recentGames: GameFromDB[];
  currentDay: number;
  seasonYear: number;
  leagueStatus: string;
  ts: number;
}

const CACHE_KEY = 'sf_home_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

function saveCache(data: CachedHomeData) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(data)); } catch {}
}

function loadCache(): CachedHomeData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as CachedHomeData;
    if (Date.now() - data.ts > CACHE_TTL) return null; // expired
    return data;
  } catch { return null; }
}

function ovrClass(ovr: number): string {
  if (ovr >= 90) return 'ovr-elite';
  if (ovr >= 80) return 'ovr-great';
  if (ovr >= 70) return 'ovr-good';
  if (ovr >= 60) return 'ovr-avg';
  return 'ovr-poor';
}

interface HomePageProps {
  isSignedIn?: boolean;
  onSignUp?: () => void;
  userData?: any;
}

const HomePage: React.FC<HomePageProps> = ({ isSignedIn = false, onSignUp, userData }) => {
  const [leagues, setLeagues] = useState<LeagueFromDB[]>([]);
  const [standings, setStandings] = useState<Map<string, StandingFromDB[]>>(new Map());
  const [teams, setTeams] = useState<Map<string, TeamFromDB>>(new Map());
  const [recentGames, setRecentGames] = useState<GameFromDB[]>([]);
  const [freeAgents, setFreeAgents] = useState<FreeAgent[]>([]);
  const [faSlide, setFaSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usingCache, setUsingCache] = useState(false);

  // Source of truth for date: league itself
  const [currentDay, setCurrentDay] = useState(1);
  const [seasonYear, setSeasonYear] = useState<number>(new Date().getFullYear());
  const [leagueStatus, setLeagueStatus] = useState('off_season');

  // League tab + carousel state
  const [selectedLeagueId, setSelectedLeagueId] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedGameId, setSelectedGameId] = useState<{gameId: string; leagueId: string} | null>(null);
  const autoRotateRef = useRef<NodeJS.Timeout | null>(null);

  // Leader state
  const [hrLeaders, setHrLeaders] = useState<LeaderPlayer[]>([]);
  const [hitLeaders, setHitLeaders] = useState<LeaderPlayer[]>([]);
  const [rbiLeaders, setRbiLeaders] = useState<LeaderPlayer[]>([]);
  const [runLeaders, setRunLeaders] = useState<any[]>([]);
  const [avgLeaders, setAvgLeaders] = useState<any[]>([]);
  const [winLeaders, setWinLeaders] = useState<LeaderPlayer[]>([]);
  const [kLeaders, setKLeaders] = useState<LeaderPlayer[]>([]);
  const [eraLeaders, setEraLeaders] = useState<LeaderPlayer[]>([]);
  const [leaderSlide, setLeaderSlide] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  // Reload games/stats when league tab changes
  useEffect(() => {
    if (selectedLeagueId && !loading) {
      loadRecentGames(selectedLeagueId);
    }
  }, [selectedLeagueId]);

  async function loadRecentGames(leagueId: string) {
    if (!isSupabaseConfigured() || !supabase) return;
    const { data: gameData } = await supabase
      .from('league_schedule')
      .select('*')
      .eq('league_id', leagueId)
      .eq('status', 'completed')
      .order('game_day', { ascending: false })
      .order('id', { ascending: false })
      .limit(20);
    if (gameData) setRecentGames(gameData as GameFromDB[]);

    // Also update the current day/status from this league
    const { data: leagueData } = await supabase.from('leagues').select('*').eq('id', leagueId).single();
    if (leagueData) {
      setCurrentDay((leagueData as any).current_day || 1);
      setSeasonYear((leagueData as any).season_year || (leagueData as any).current_year || new Date().getFullYear());
      setLeagueStatus((leagueData as any).status || 'off_season');
    }
  }

  // Auto-rotate carousel
  useEffect(() => {
    if (!isPaused && getSlideCount() > 1) {
      autoRotateRef.current = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % getSlideCount());
      }, 5000);
    }
    return () => {
      if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    };
  }, [isPaused, leagues, standings]);

  async function loadData() {
    setLoading(true);

    // Try loading from Supabase first
    let loaded = false;
    if (isSupabaseConfigured() && supabase) {
      try {
        loaded = await loadFromSupabase();
      } catch (e) {
        console.warn('Supabase load failed, trying cache:', e);
      }
    }

    // If Supabase failed, try cache
    if (!loaded) {
      const cache = loadCache();
      if (cache) {
        setLeagues(cache.leagues);
        setStandings(new Map(cache.standings));
        setTeams(new Map(cache.teams));
        setRecentGames(cache.recentGames);
        setCurrentDay(cache.currentDay);
        setSeasonYear(cache.seasonYear);
        setLeagueStatus(cache.leagueStatus);
        setUsingCache(true);
      }
    }

    setLoading(false);
  }

  async function loadFromSupabase(): Promise<boolean> {
    if (!isSupabaseConfigured() || !supabase) return false;

    // Load leagues first — only active ones (status != off_season)
    const leagueRes = await supabase
      .from('leagues')
      .select('*')
      .neq('hidden', true)
      .order('tier', { ascending: true });

    // Only fetch teams that belong to these leagues — avoids 1000-row PostgREST cap (2328 teams in DB)
    const leagueIds = (leagueRes.data || []).map((l: any) => l.id);
    const teamRes = leagueIds.length > 0
      ? await supabase.from('teams').select('id, name, city, abbreviation').in('league_id', leagueIds)
      : { data: [], error: null };

    const teamData = teamRes.data;
    const leagueData = leagueRes.data;

    if (!leagueData || leagueData.length === 0) return false;

    // Process teams
    if (teamData) {
      const map = new Map<string, TeamFromDB>();
      (teamData as TeamFromDB[]).forEach(t => map.set(t.id, t));
      setTeams(map);
    }

    // Process leagues
    setLeagues(leagueData as LeagueFromDB[]);
    // Default to lowest tier (best) league — tier 1 = Major League
    const tradLeagues = (leagueData as LeagueFromDB[]).filter(l => TRAD_ARRAY.includes(l.league_type || ''));
    tradLeagues.sort((a, b) => (a.tier || 99) - (b.tier || 99));
    const traditionalLeague = tradLeagues[0];
    if (!selectedLeagueId) {
      if (traditionalLeague) setSelectedLeagueId(traditionalLeague.id);
    }
    if (traditionalLeague) {
      setCurrentDay(traditionalLeague.current_day || 1);
      setSeasonYear(traditionalLeague.season_year || new Date().getFullYear());
      setLeagueStatus(traditionalLeague.status || 'off_season');
    }

    // Now fire all dependent queries in parallel
    const currentSeasonYear = traditionalLeague?.season_year || new Date().getFullYear();
    const activeLeagueId = selectedLeagueId || traditionalLeague?.id;

    const [standingRes, gameRes, faRes] = await Promise.all([
      supabase.from('league_standings').select('*').eq('season_year', currentSeasonYear),
      activeLeagueId
        ? supabase.from('league_schedule').select('*').eq('league_id', activeLeagueId).eq('status', 'completed').order('game_day', { ascending: false }).order('id', { ascending: false }).limit(20)
        : Promise.resolve({ data: null, error: null }),
      supabase.from('players')
        .select('id, first_name, last_name, position, overall, is_hitter, power, contact, speed, fielding, velocity, control, movement, endurance')
        .is('team_id', null)
        .eq('retired', false)
        .order('overall', { ascending: false })
        .limit(20),
    ]);

    // Process standings
    const standingData = standingRes.data;
    const grouped = new Map<string, StandingFromDB[]>();
    if (standingData) {
      (standingData as StandingFromDB[]).forEach(s => {
        const key = s.league_id || 'unassigned';
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(s);
      });
      setStandings(grouped);
    }

    // Process games
    const gameData = gameRes.data;
    if (gameData) setRecentGames(gameData as GameFromDB[]);

    // Process free agents
    const faData = faRes.data;
    if (faData) setFreeAgents(faData as FreeAgent[]);

    // Load stat leaders in parallel (non-blocking — don't await to unblock render)
    loadStatLeaders(leagueData as LeagueFromDB[]);

    // Save to cache for offline resilience
    if (traditionalLeague) {
      saveCache({
        leagues: leagueData as LeagueFromDB[],
        standings: Array.from(grouped.entries()),
        teams: Array.from((teamData ? new Map((teamData as TeamFromDB[]).map(t => [t.id, t])) : new Map()).entries()),
        recentGames: (gameData || []) as GameFromDB[],
        currentDay: traditionalLeague.current_day || 1,
        seasonYear: traditionalLeague.season_year || new Date().getFullYear(),
        leagueStatus: traditionalLeague.status || 'off_season',
        ts: Date.now(),
      });
    }

    setUsingCache(false);
    return true;
  }

  async function loadStatLeaders(leagueOverride?: LeagueFromDB[]) {
    if (!isSupabaseConfigured() || !supabase) return;

    const leagueSource = leagueOverride || leagues;
    const tradLeagues = leagueSource.filter((l: any) => TRAD_ARRAY.includes(l.league_type || ''));
    tradLeagues.sort((a: any, b: any) => (a.tier || 99) - (b.tier || 99));
    const traditionalLeague = tradLeagues[0];
    const currentYear = traditionalLeague?.season_year || seasonYear;

    const { data: statsData } = await supabase
      .from('player_season_stats')
      .select('*')
      .eq('season_year', currentYear)
      .gt('games', 0)
      .limit(200);

    if (!statsData || statsData.length === 0) return;

    // Batch load player names in parallel
    const playerIds = Array.from(new Set(statsData.map((s: any) => s.player_id)));
    let playerMap = new Map<string, any>();
    if (playerIds.length > 0) {
      const batchSize = 50;
      const batches: string[][] = [];
      for (let i = 0; i < playerIds.length; i += batchSize) {
        batches.push(playerIds.slice(i, i + batchSize));
      }
      const batchResults = await Promise.all(
        batches.map(batch => supabase!.from('players').select('id, first_name, last_name, position, team_id').in('id', batch))
      );
      batchResults.forEach(res => {
        if (res.data) res.data.forEach((p: any) => playerMap.set(String(p.id), p));
      });
    }

    const all = statsData.map((s: any) => {
      const player = playerMap.get(String(s.player_id)) || playerMap.get(s.player_id) || null;
      return { ...s, players: player };
    }) as LeaderPlayer[];

    const isPitcherPos = (pos: string) => {
      const p = (pos || '').toUpperCase();
      return p === 'P';
    };

    setHrLeaders(all.filter(p => !isPitcherPos(p.players?.position || '')).sort((a, b) => (b.home_runs || 0) - (a.home_runs || 0)).slice(0, 5));
    setHitLeaders(all.filter(p => !isPitcherPos(p.players?.position || '')).sort((a, b) => (b.hits || 0) - (a.hits || 0)).slice(0, 5));
    setRbiLeaders(all.filter(p => !isPitcherPos(p.players?.position || '')).sort((a, b) => (b.rbi || 0) - (a.rbi || 0)).slice(0, 5));
    setRunLeaders(all.filter(p => !isPitcherPos(p.players?.position || '')).sort((a, b) => (b.runs || 0) - (a.runs || 0)).slice(0, 5));
    setAvgLeaders(all
      .filter(p => !isPitcherPos(p.players?.position || '') && (p.at_bats || 0) >= 10)
      .map(p => ({ ...p, _avg: (p.at_bats || 0) > 0 ? (p.hits || 0) / (p.at_bats || 1) : 0 }))
      .sort((a: any, b: any) => b._avg - a._avg)
      .slice(0, 5)
    );
    setWinLeaders(all.filter(p => (p.innings_pitched || 0) > 0).sort((a, b) => (b.wins || 0) - (a.wins || 0)).slice(0, 5));
    setKLeaders(all.filter(p => (p.innings_pitched || 0) > 0).sort((a, b) => (b.strikeouts_pitched || 0) - (a.strikeouts_pitched || 0)).slice(0, 5));
    setEraLeaders(all
      .filter(p => (p.innings_pitched || 0) >= 5)
      .sort((a, b) => {
        const eraA = (a.innings_pitched || 0) > 0 ? ((a.earned_runs || 0) * 9) / (a.innings_pitched || 1) : 99.99;
        const eraB = (b.innings_pitched || 0) > 0 ? ((b.earned_runs || 0) * 9) / (b.innings_pitched || 1) : 99.99;
        return eraA - eraB;
      })
      .slice(0, 5)
    );
  }

  const getSlideCount = (): number => {
    let count = 0;
    const filtered = selectedLeagueId
      ? leagues.filter(l => l.id === selectedLeagueId)
      : leagues.filter(l => TRAD_ARRAY.includes(l.league_type || ''));
    filtered.forEach(league => {
      const leagueStandings = standings.get(league.id) || [];
      const conferences = new Set(leagueStandings.map(s => s.conference));
      count += conferences.size || 1;
    });
    return Math.max(count, 1);
  };

  const getSlides = () => {
    const slides: { leagueName: string; leagueId: string; leagueStatus: string; leagueTier: number; conference: string; divisionStandings: Map<string, StandingFromDB[]> }[] = [];
    const filtered = selectedLeagueId
      ? leagues.filter(l => l.id === selectedLeagueId)
      : leagues.filter(l => TRAD_ARRAY.includes(l.league_type || ''));
    filtered.forEach(league => {
      const leagueStandings = standings.get(league.id) || [];
      const byConf = new Map<string, StandingFromDB[]>();
      leagueStandings.forEach(s => {
        const conf = s.conference || 'Unassigned';
        if (!byConf.has(conf)) byConf.set(conf, []);
        byConf.get(conf)!.push(s);
      });
      byConf.forEach((confStandings, conference) => {
        const byDiv = new Map<string, StandingFromDB[]>();
        confStandings.forEach(s => {
          const div = s.division || 'Unassigned';
          if (!byDiv.has(div)) byDiv.set(div, []);
          byDiv.get(div)!.push(s);
        });
        slides.push({ leagueName: league.name, leagueId: league.id, leagueStatus: league.status, leagueTier: league.tier, conference, divisionStandings: byDiv });
      });
    });
    return slides;
  };

  const getTeamName = (teamId: string): string => {
    const team = teams.get(teamId);
    if (!team) return 'Unknown';
    return team.name.startsWith(team.city) ? team.name : `${team.city} ${team.name}`;
  };

  const getTeamAbbr = (teamId: string): string => {
    const team = teams.get(teamId);
    return team?.abbreviation || getTeamName(teamId).split(' ').pop() || '???';
  };

  const calcPct = (w: number, l: number): string => {
    const total = w + l;
    return total > 0 ? (w / total).toFixed(3) : '.000';
  };

  const calcEra = (er: number, ip: number): string => {
    return ip > 0 ? ((er * 9) / ip).toFixed(2) : '-.--';
  };

  // Derive the display date from the league's own current_day
  function getDisplayDate(): string {
    const st = leagueStatus as 'active' | 'playoffs' | 'off_season';
    const d = gameDayToDate(currentDay, seasonYear, st);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dateStr = `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${seasonYear}`;
    if (leagueStatus === 'playoffs') return `Playoffs — ${dateStr}`;
    if (leagueStatus === 'off_season') return `Off-Season — ${dateStr}`;
    return dateStr;
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_season': return <span className="sf-badge sf-badge-green">LIVE</span>;
      case 'playoffs': return <span className="sf-badge sf-badge-gold">PLAYOFFS</span>;
      case 'off_season': return <span className="sf-badge sf-badge-muted">OFF-SEASON</span>;
      default: return <span className="sf-badge sf-badge-muted">{status?.toUpperCase()}</span>;
    }
  };

  const getLeaderSlides = () => {
    return [
      { label: 'Batting Average', icon: '📊', data: avgLeaders.map((p: any) => ({ ...p, _displayStat: (p._avg || 0).toFixed(3).replace(/^0/, '') })) },
      { label: 'Home Runs', icon: '💪', data: hrLeaders.map(p => ({ ...p, _displayStat: p.home_runs || 0 })) },
      { label: 'RBI', icon: '💰', data: rbiLeaders.map(p => ({ ...p, _displayStat: p.rbi || 0 })) },
      { label: 'Runs', icon: '🏃', data: runLeaders.map((p: any) => ({ ...p, _displayStat: p.runs || 0 })) },
      { label: 'Wins', icon: '🏆', data: winLeaders.map(p => ({ ...p, _displayStat: p.wins || 0 })) },
      { label: 'ERA', icon: '🔥', data: eraLeaders.map((p: any) => ({ ...p, _displayStat: calcEra(p.earned_runs || 0, p.innings_pitched || 0) })) },
      { label: 'Strikeouts', icon: '⚡', data: kLeaders.map(p => ({ ...p, _displayStat: p.strikeouts_pitched || 0 })) },
    ];
  };

  const slides = getSlides();
  const leaderSlides = getLeaderSlides();

  // Count total games played
  const totalGamesPlayed = recentGames.length;
  const homeWins = recentGames.filter(g => g.winner === g.home_team_id).length;
  const awayWins = recentGames.filter(g => g.winner === g.away_team_id).length;

  if (loading) {
    return (
      <div className="sf-page">
        <div className="sf-loading">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              border: '3px solid rgba(200,150,26,0.15)',
              borderTopColor: '#e8b82a',
              animation: 'sf-spin 0.8s linear infinite'
            }} />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading SimForge...</span>
          </div>
        </div>
        <style>{`@keyframes sf-spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return (
    <div className="sf-page">

      {/* === SCORE TICKER === */}
      {recentGames.length > 0 && (
        <div className="score-ticker">
          <div className="ticker-scroll">
            {recentGames.concat(recentGames).map((game, idx) => {
              const homeWon = game.winner === game.home_team_id;
              return (
                <span
                  key={`${game.id}-${idx}`}
                  className="ticker-game ticker-game-clickable"
                  onClick={() => {
                    const gid = (game as any).game_id || game.id;
                    setSelectedGameId({ gameId: gid, leagueId: game.league_id });
                  }}
                >
                  <span className="ticker-league">{gameDayToShortDate(game.game_day, seasonYear)}</span>
                  <span className="ticker-teams">
                    {getTeamAbbr(game.away_team_id)}{' '}
                    <span className={`ticker-score ${!homeWon ? 'winner' : 'loser'}`}>{game.away_score}</span>
                    {' @ '}
                    {getTeamAbbr(game.home_team_id)}{' '}
                    <span className={`ticker-score ${homeWon ? 'winner' : 'loser'}`}>{game.home_score}</span>
                  </span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* === SIM DATE BANNER === */}
      {currentDay > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '14px 20px',
          marginBottom: 16,
          background: 'linear-gradient(135deg, rgba(212,160,23,0.08) 0%, rgba(212,160,23,0.03) 100%)',
          border: '1px solid rgba(212,160,23,0.15)',
          borderRadius: 10,
        }}>
          <span style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: '#e8b82a',
            letterSpacing: '0.01em',
          }}>
            {(() => {
              const league = leagues.filter(l => TRAD_ARRAY.includes(l.league_type || ''))[0];
              if (!league) return '—';
              // Use sim_date if available (most accurate); fall back to day calculation
              const dateStr = (league as any).sim_date
                ? formatSimDate((league as any).sim_date)
                : (() => {
                    const yr = league.season_year || seasonYear;
                    const day = league.current_day || 1;
                    const st = (league.status || 'active') as 'active' | 'playoffs' | 'off_season';
                    const d = gameDayToDate(day, yr, st);
                    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  })();
              if (league.status === 'off_season') return `Off-Season — ${dateStr}`;
              if (league.status === 'playoffs') return `Playoffs — ${dateStr}`;
              return dateStr;
            })()}
          </span>
        </div>
      )}

      {/* === STANDINGS — FULL WIDTH === */}
      <div className="sf-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
        {/* League Tabs */}
        {leagues.filter(l => TRAD_ARRAY.includes(l.league_type || '')).length > 1 && (
          <div style={{
            display: 'flex', gap: 0, borderBottom: '1px solid var(--border-primary)',
            overflowX: 'auto', scrollbarWidth: 'none'
          }}>
            {leagues
              .filter(l => TRAD_ARRAY.includes(l.league_type || ''))
              .sort((a, b) => (a.tier || 5) - (b.tier || 5))
              .map(league => {
                const isActive = selectedLeagueId === league.id;
                const tierColors: Record<number, { bg: string; color: string }> = {
                  1: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b' },     // Premier
                  2: { bg: 'rgba(139,92,246,0.12)', color: '#a78bfa' },      // Major
                  3: { bg: 'rgba(59,130,246,0.12)', color: '#60a5fa' },      // AAA
                  4: { bg: 'rgba(16,185,129,0.12)', color: '#34d399' },      // AA
                  5: { bg: 'rgba(156,163,175,0.12)', color: '#94a3b8' },     // Rookie
                };
                const tc = tierColors[league.tier] || tierColors[5];
                return (
                  <button
                    key={league.id}
                    onClick={() => { setSelectedLeagueId(league.id); setCurrentSlide(0); setIsPaused(false); }}
                    style={{
                      padding: '10px 18px',
                      background: isActive ? tc.bg : 'transparent',
                      color: isActive ? tc.color : 'var(--text-muted)',
                      border: 'none',
                      borderBottom: isActive ? `2px solid ${tc.color}` : '2px solid transparent',
                      fontWeight: isActive ? 700 : 500,
                      fontSize: '0.8rem',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                      letterSpacing: '0.02em',
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => { if (!isActive) { e.currentTarget.style.color = tc.color; e.currentTarget.style.background = tc.bg; } }}
                    onMouseLeave={e => { if (!isActive) { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.background = 'transparent'; } }}
                  >
                    {['','Premier','Major','AAA','AA','Rookie'][league.tier] || `Tier ${league.tier}`}
                  </button>
                );
              })}
          </div>
        )}
        <div className="sf-card-header" style={{ padding: '16px 20px', marginBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="sf-card-title">
              {slides[currentSlide]?.leagueName || 'League Standings'}
            </span>
            {slides[currentSlide]?.leagueTier && (
              <span className="sf-badge" style={{
                background: slides[currentSlide].leagueTier <= 2 ? 'rgba(212,160,23,0.15)' :
                            slides[currentSlide].leagueTier <= 3 ? 'rgba(139,92,246,0.15)' :
                            'rgba(100,116,139,0.15)',
                color: slides[currentSlide].leagueTier <= 2 ? '#e8b82a' :
                       slides[currentSlide].leagueTier <= 3 ? '#a78bfa' :
                       '#94a3b8',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
              }}>
                {['','Premier','Major','AAA','AA','Rookie'][slides[currentSlide].leagueTier] || `Tier ${slides[currentSlide].leagueTier}`}
              </span>
            )}
            {slides[currentSlide]?.conference && (
              <span className="sf-badge sf-badge-muted">{slides[currentSlide].conference}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              className="sf-btn sf-btn-ghost"
              style={{ padding: '4px 10px', fontSize: '1rem', minWidth: 32 }}
              onClick={() => {
                setIsPaused(true);
                setCurrentSlide((currentSlide - 1 + slides.length) % slides.length);
              }}
            >
              ‹
            </button>
            <div style={{ display: 'flex', gap: 4 }}>
              {slides.map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => { setIsPaused(true); setCurrentSlide(idx); }}
                  style={{
                    width: idx === currentSlide ? 18 : 6,
                    height: 6,
                    borderRadius: 3,
                    background: idx === currentSlide ? 'var(--accent-primary)' : 'rgba(200,150,26,0.2)',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                />
              ))}
            </div>
            <button
              className="sf-btn sf-btn-ghost"
              style={{ padding: '4px 10px', fontSize: '1rem', minWidth: 32 }}
              onClick={() => {
                setIsPaused(true);
                setCurrentSlide((currentSlide + 1) % slides.length);
              }}
            >
              ›
            </button>
          </div>
        </div>

        <div style={{ padding: '0 0 8px' }}>
          {slides[currentSlide] && Array.from(slides[currentSlide].divisionStandings.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([division, divStandings]) => {
              // Sort by win pct descending, then wins descending
              const sorted = [...divStandings].sort((a, b) => {
                const pctA = a.wins + a.losses > 0 ? a.wins / (a.wins + a.losses) : 0;
                const pctB = b.wins + b.losses > 0 ? b.wins / (b.wins + b.losses) : 0;
                if (pctB !== pctA) return pctB - pctA;
                return b.wins - a.wins;
              });
              // Calculate GB for each team (Games Behind division leader)
              const leader = sorted[0];
              const calcGB = (s: StandingFromDB) => {
                if (!leader || s.team_id === leader.team_id) return '-';
                const leaderDiff = leader.wins - leader.losses;
                const teamDiff = s.wins - s.losses;
                const gb = (leaderDiff - teamDiff) / 2;
                return gb === 0 ? '-' : gb.toFixed(1);
              };

              return (
                <div key={division} style={{ marginBottom: 8 }}>
                  <div style={{
                    padding: '6px 20px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: 'var(--accent-primary-light)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    background: 'rgba(200,150,26,0.04)'
                  }}>
                    {division}
                  </div>
                  <div className="sf-table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
                    <table className="sf-table" style={{ width: '100%' }}>
                      <thead>
                        <tr>
                          <th style={{ paddingLeft: 20, width: 40 }}>#</th>
                          <th style={{ textAlign: 'left' }}>Team</th>
                          <th>W</th>
                          <th>L</th>
                          <th>PCT</th>
                          <th>GB</th>
                          <th>RS</th>
                          <th>RA</th>
                          <th>DIFF</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((s, idx) => {
                          const rd = s.runs_scored - s.runs_allowed;
                          return (
                            <tr key={s.team_id}>
                              <td style={{ paddingLeft: 20, color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.82rem' }}>
                                {idx + 1}
                              </td>
                              <td
                                style={{ textAlign: 'left', color: 'var(--text-primary)', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/team/${s.team_id}` }))}
                                onMouseEnter={e => (e.currentTarget.style.color = 'var(--accent-primary-light)')}
                                onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-primary)')}
                              >
                                {getTeamName(s.team_id)}
                              </td>
                              <td style={{ color: 'var(--accent-green)', fontWeight: 700 }}>{s.wins}</td>
                              <td style={{ color: 'var(--accent-red)' }}>{s.losses}</td>
                              <td style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{calcPct(s.wins, s.losses)}</td>
                              <td style={{ color: calcGB(s) === '-' ? 'var(--text-muted)' : 'var(--text-primary)', fontSize: '0.85rem' }}>{calcGB(s)}</td>
                              <td>{s.runs_scored}</td>
                              <td>{s.runs_allowed}</td>
                              <td style={{ fontWeight: 600, color: rd > 0 ? 'var(--accent-green)' : rd < 0 ? 'var(--accent-red)' : 'var(--text-muted)' }}>
                                {rd > 0 ? '+' : ''}{rd}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {/* === RECENT RESULTS — COMPACT GRID BELOW STANDINGS === */}
      <div className="sf-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 24 }}>
        <div className="sf-card-header" style={{ padding: '16px 20px', marginBottom: 0 }}>
          <span className="sf-card-title">Recent Results {selectedLeagueId && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>· {leagues.find(l => l.id === selectedLeagueId)?.name || ''}</span>}</span>
          <span className="sf-badge sf-badge-muted">{recentGames.length} games</span>
        </div>
        <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {recentGames.length > 0 ? recentGames.map(game => {
            const homeWon = game.winner === game.home_team_id;
            return (
              <div
                key={game.id}
                onClick={() => setSelectedGameId({ gameId: game.game_id ?? game.id, leagueId: game.league_id })}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px',
                  background: 'rgba(16,24,40,0.4)',
                  border: '1px solid var(--border-primary)',
                  borderRadius: 8,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  minWidth: 180
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.background = 'rgba(200,150,26,0.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.background = 'rgba(16,24,40,0.4)'; }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 80 }}>
                  <span style={{
                    fontSize: '0.78rem', fontWeight: 600,
                    color: homeWon ? 'var(--text-primary)' : 'var(--text-muted)'
                  }}>
                    {getTeamAbbr(game.home_team_id)}
                  </span>
                  <span style={{
                    fontSize: '0.78rem', fontWeight: 600,
                    color: !homeWon ? 'var(--text-primary)' : 'var(--text-muted)'
                  }}>
                    {getTeamAbbr(game.away_team_id)}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
                  <span style={{
                    fontSize: '0.88rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                    color: game.home_score > game.away_score ? 'var(--accent-primary-light)' : 'var(--text-muted)'
                  }}>
                    {game.home_score}
                  </span>
                  <span style={{
                    fontSize: '0.88rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                    color: game.away_score > game.home_score ? 'var(--accent-primary-light)' : 'var(--text-muted)'
                  }}>
                    {game.away_score}
                  </span>
                </div>
                <span style={{
                  fontSize: '0.65rem', color: 'var(--text-muted)',
                  marginLeft: 4, whiteSpace: 'nowrap'
                }}>
                  {gameDayToShortDate(game.game_day, seasonYear)}
                </span>
              </div>
            );
          }) : (
            <div className="sf-empty" style={{ padding: 32, width: '100%' }}>
              <span className="sf-empty-text">No games yet — results appear once the season begins.</span>
            </div>
          )}
        </div>
      </div>

      {/* === BOTTOM GRID: Leaders + Free Agents === */}
      <div className="sf-grid-2">

        {/* STAT LEADERS CARD */}
        <div className="sf-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="sf-card-header" style={{ padding: '16px 20px', marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem' }}>{leaderSlides[leaderSlide]?.icon}</span>
              <span className="sf-card-title">{leaderSlides[leaderSlide]?.label || 'Stat Leaders'}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                className="sf-btn sf-btn-ghost"
                style={{ padding: '4px 10px', fontSize: '1rem', minWidth: 28 }}
                onClick={() => setLeaderSlide((leaderSlide - 1 + leaderSlides.length) % leaderSlides.length)}
              >
                ‹
              </button>
              <div style={{ display: 'flex', gap: 4 }}>
                {leaderSlides.map((_, i) => (
                  <div
                    key={i}
                    onClick={() => setLeaderSlide(i)}
                    style={{
                      width: i === leaderSlide ? 16 : 5,
                      height: 5,
                      borderRadius: 3,
                      background: i === leaderSlide ? 'var(--accent-primary)' : 'rgba(200,150,26,0.2)',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  />
                ))}
              </div>
              <button
                className="sf-btn sf-btn-ghost"
                style={{ padding: '4px 10px', fontSize: '1rem', minWidth: 28 }}
                onClick={() => setLeaderSlide((leaderSlide + 1) % leaderSlides.length)}
              >
                ›
              </button>
            </div>
          </div>

          <div style={{ padding: '4px 0' }}>
            {(leaderSlides[leaderSlide]?.data || []).map((p: any, i: number) => (
              <div
                key={p.player_id || i}
                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${p.player_id}` }))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 20px',
                  borderBottom: '1px solid var(--border-primary)',
                  cursor: 'pointer',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,150,26,0.03)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{
                    width: 24, height: 24, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: 800,
                    background: i === 0 ? 'var(--gradient-primary)' : i < 3 ? 'rgba(200,150,26,0.15)' : 'rgba(107,114,128,0.15)',
                    color: i === 0 ? '#0a0e17' : i < 3 ? 'var(--accent-primary-light)' : 'var(--text-muted)'
                  }}>
                    {i + 1}
                  </span>
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.88rem' }}>
                      {p.players?.first_name || p.first_name
                        ? `${p.players?.first_name || p.first_name} ${p.players?.last_name || p.last_name}`
                        : `Player ${p.player_id?.slice(-6) || '???'}`}
                    </span>
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginLeft: 8 }}>
                      {p.players?.position || p.position || ''}
                    </span>
                  </div>
                </div>
                <span style={{
                  fontWeight: 800, fontSize: '1rem', fontVariantNumeric: 'tabular-nums',
                  color: i === 0 ? 'var(--accent-primary-light)' : 'var(--text-primary)'
                }}>
                  {p._displayStat}
                </span>
              </div>
            ))}
            {(!leaderSlides[leaderSlide]?.data || leaderSlides[leaderSlide]?.data.length === 0) && (
              <div className="sf-empty" style={{ padding: 32 }}>
                <span className="sf-empty-text">No stats yet — leaders appear after games are played.</span>
              </div>
            )}
          </div>
        </div>

        {/* FREE AGENTS CARD */}
        <div className="sf-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="sf-card-header" style={{ padding: '16px 20px', marginBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem' }}>🆓</span>
              <span className="sf-card-title">Top Free Agents</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                className="sf-btn sf-btn-ghost"
                style={{ padding: '4px 10px', fontSize: '1rem', minWidth: 28 }}
                onClick={() => setFaSlide(Math.max(0, faSlide - 5))}
              >
                ‹
              </button>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {faSlide + 1}-{Math.min(faSlide + 5, freeAgents.length)} of {freeAgents.length}
              </span>
              <button
                className="sf-btn sf-btn-ghost"
                style={{ padding: '4px 10px', fontSize: '1rem', minWidth: 28 }}
                onClick={() => setFaSlide(Math.min(Math.max(0, freeAgents.length - 5), faSlide + 5))}
              >
                ›
              </button>
            </div>
          </div>

          <div style={{ padding: '4px 0' }}>
            {freeAgents.slice(faSlide, faSlide + 5).map(fa => (
              <div
                key={fa.id}
                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${fa.id}` }))}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 20px',
                  borderBottom: '1px solid var(--border-primary)',
                  cursor: 'pointer',
                  transition: 'background 0.15s'
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(200,150,26,0.03)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.88rem' }}>
                      {fa.first_name} {fa.last_name}
                    </span>
                    <span style={{ marginLeft: 8 }}>
                      <span className="sf-badge sf-badge-muted" style={{ fontSize: '0.65rem', padding: '1px 6px' }}>
                        {fa.position}
                      </span>
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {fa.is_hitter ? (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      PWR {fa.power} · CON {fa.contact}
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                      VEL {fa.velocity} · CTL {fa.control}
                    </span>
                  )}
                  <span className={ovrClass(fa.overall)} style={{ fontSize: '1.05rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                    {numberToGrade(fa.overall)}
                  </span>
                </div>
              </div>
            ))}
            {freeAgents.length === 0 && (
              <div className="sf-empty" style={{ padding: 32 }}>
                <span className="sf-empty-text">No free agents available right now.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* BoxScore Modal */}
      {selectedGameId && (
        <BoxScore gameId={selectedGameId.gameId} leagueId={selectedGameId.leagueId} onClose={() => setSelectedGameId(null)} />
      )}
    </div>
  );
};

export default HomePage;
